import React, { createRef, RefObject } from 'react';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import Board from 'react-trello';
import MyCard from './Card';
import { ITasks, IUsers, RootState } from 'store/store';
import { DeleteState, ISearchParams, Order } from 'store/entity/types';
import messages from './Messages';
import Icon from 'components/Icon';
import Button from 'components/Button';
import TasksList from './TasksList';
import TaskReport from 'components/Report/TaskReport/TaskReport';
import { display } from 'components/ErrorView/ErrorDialog';
import ButtonGroup from 'components/ButtonGroup';
import TemplateSelectorList, {
  getFieldOptions,
  ITemplateTaskCounts,
} from './TemplateSelectorList';
import { Container, Header, ListOptions } from './styles';
import APopover from 'components/APopover';
import { copyTaskListToClipboard } from './TaskUtils';
import {
  isANumber,
  convertDigitToProperFormat,
  preprocessForSearch,
  englishNumber,
} from 'utils/number';
import {
  InformativeBadge,
  TaskOptionsIcon,
  DropdownPrintIcon,
  PrintLink,
  PrintLoaderContainer,
  PrintLoaderSpinner,
} from 'components/TaskEditorHeader/styles';
import CalendarView from './CalendarView';
import {
  endOfDate,
  jMonthIndex,
  monthIndexToTimestamp,
  smartDateDuration,
  startOfDate,
} from 'utils/date';
import UserSelector from 'components/UserSelector';
import { IRoleOption, IUserOption } from 'components/UserSelector/UserSelector';
import {
  black,
  exactWhite,
  green,
  lightGreen,
  stringToColour,
} from 'utils/style/colors';
import generateUserPermission from 'utils/permission';
import notify from 'components/Notify';
import * as taskManipulate from 'store/tasks/taskManipulate';
import moment, { JUnitOfTime } from 'moment-jalaali';
import Select, { IOption } from 'components/Select/Select';
import { withTitle } from 'components/withTitle';
import { SwitchWrapper } from 'components/Switch/style';
import { removeDuplicates } from 'utils/happyFunctions';
import { appDispatch } from 'store/store';
import {
  markTaskAssignment,
  searchTasks,
  showDeletedAction,
  updateTask,
  updateTaskFn,
  getTaskReport,
} from 'store/tasks/tasksSlice';
import {
  HiddenFieldValue,
  IAssignmentView,
  IField,
  ITask,
  TaskStatus,
} from 'store/tasks/tasksType';
import { ITemplateReportMark } from 'store/templates/templatesSlice';
import { IThisUserState, IUserFlag } from 'store/thisUser/thisUserSlice';
import {
  ICompany,
  ICompanyMemberRole,
  IProjectMemberView,
  TProjects,
} from 'store/companies/companiesTypes';
import {
  isCompanyAdmin,
  isCompanyOwnerOrSupport,
} from 'store/companies/companiesUtils';
import Input from '../../../components/Input';
import ActionLink from '../../../components/ActionLink/ActionLink';
import i18n, {
  getCalendarLocal,
  getLangBaseOnLocal,
  getLocalBaseOnLang,
  LanguageList,
} from '../../../i18n';
import { Menu, MenuItem } from '../../../components/Dropdown/styles';
import { LoadingCircleIndicatorNoPad } from '../../../components/LoadingCircleIndicator/LoadingCircleIndicator';
import { ILocal } from '../../../components/Chart/BaseDatePicker';
import { getLanguageBaseOnTZ } from '../../../utils/LanguageLocalStorage';
import { StepType, TourContext } from '@reactour/tour';

const pageSize = 20;
const largePageSize = 50;
const largeScreenSize = 1040;
interface IStateToProps {
  tasks: ITasks['data'];
  showDeleted: boolean;
  projects: TProjects;
  thisUser: IThisUserState;
  dirtySet: number[];
  company: ICompany;
  users: IUsers['map'];
  companyOwnerId: number;
  tourFlags: Array<IUserFlag>;
}

interface IDispatchToProps {
  updateTask: (task: ITask) => ReturnType<typeof updateTaskFn>;
}

interface IOwnProps {
  companyId: number;
  taskSelected?: number | boolean;
  onOpenTaskEditor: (
    taskId: number | 'new',
    templateId?: number,
    boardField?: any,
    selectedBoardFieldTitle?: string
  ) => any;
  endDate?: number;
  parentId?: number;
  ancestorId?: number;
  projectId?: number;
  startDate?: number;
  templateId?: number;
  assigneeId?: number;
  reportParams?: any;
  archivedDefault?: boolean;
  includeDrafted?: boolean;
  providerItemId?: number;
  withFewerOptions?: boolean;
  fieldValues?: { [key: string]: string };
  local?: ILocal;
  isModal: boolean;
}

interface IComponentStates {
  from: number;
  order: Order;
  loading: boolean;
  loadingExtraTasks: boolean;
  archived: boolean;
  loadingCopyTask: boolean;
  sortType: 'asc' | 'desc';
  whichTasks:
    | 'all'
    | 'assignedToMe'
    | 'createdByMe'
    | 'categorized'
    | 'mentions'
    | number;
  taskStatus: 'all' | TaskStatus;
  showReport: boolean;
  showCalendarView: boolean;
  showProgress: boolean;
  activeProjectId?: number;
  allTasksFetched: boolean;
  fieldValues: { [key: string]: string };
  basedOnTemplate: 'all' | number;
  calendarStartDate?: Date;
  calendarEndDate?: Date;
  selectedAssigneeId?: number;
  assigneeSelectOpen: boolean;
  members?: {
    [key: number]: IUserOption | IRoleOption;
  };
  [key: string]: any;
  boardField?: IField;
  selectedDateFilter?: IOption & { startDate?: number; endDate?: number };
  searchTaskContent: string;
  printLoader: boolean;
  selectedMonthFilter?: string;
  windowWidth: number;
  laneLimits: Record<string, number>;
  fetchIdList: Array<number>;
  taskCounts: ITemplateTaskCounts | null;
}

type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

function getDateFilterOptions(
  local: ILocal = getLocalBaseOnLang(
    getLanguageBaseOnTZ() as LanguageList
  ) as ILocal
): IOption[] {
  const mYear = local === 'fa_IR@calendar=persian' ? 'jYear' : 'year';
  const fullYear = local === 'fa_IR@calendar=persian' ? 'jYYYY' : 'YYYY';
  const dateFilterPicker = [];
  let endOfCurrentYear =
    local === 'fa_IR@calendar=persian'
      ? moment(endOfDate(new Date(), 'year', true, getLangBaseOnLocal(local)))
      : moment(
          endOfDate(new Date(), 'year', true, getLangBaseOnLocal(local))
        ).locale('en');
  let startOfCurrentYear =
    local === 'fa_IR@calendar=persian'
      ? moment(startOfDate(new Date(), 'year', true, getLangBaseOnLocal(local)))
      : moment(
          startOfDate(new Date(), 'year', true, getLangBaseOnLocal(local))
        ).locale('en');
  const middleOfNextYear = new Date(
    (local === 'fa_IR@calendar=persian'
      ? moment(
          (endOfCurrentYear.toDate().getTime() +
            startOfCurrentYear.toDate().getTime()) /
            2
        )
      : moment(
          (endOfCurrentYear.toDate().getTime() +
            startOfCurrentYear.toDate().getTime()) /
            2
        ).locale('en')
    )
      .add(1, mYear as JUnitOfTime)
      .toDate()
      .getTime()
  );
  // add another year:
  endOfCurrentYear =
    local === 'fa_IR@calendar=persian'
      ? moment(
          endOfDate(middleOfNextYear, 'year', true, getLangBaseOnLocal(local))
        )
      : moment(
          endOfDate(middleOfNextYear, 'year', true, getLangBaseOnLocal(local))
        ).locale('en');
  startOfCurrentYear =
    local === 'fa_IR@calendar=persian'
      ? moment(
          startOfDate(middleOfNextYear, 'year', true, getLangBaseOnLocal(local))
        )
      : moment(
          startOfDate(middleOfNextYear, 'year', true, getLangBaseOnLocal(local))
        ).locale('en');

  for (let i = 0; i < 10; i++) {
    dateFilterPicker.push({
      label: convertDigitToProperFormat(startOfCurrentYear.format(fullYear)),
      value: i,
      startDate: startOfCurrentYear.valueOf(),
      endDate: i === 0 ? undefined : endOfCurrentYear.valueOf(),
    } as IOption);
    endOfCurrentYear.subtract(
      1,
      mYear as moment.unitOfTime.DurationConstructor
    );
    startOfCurrentYear.subtract(
      1,
      mYear as moment.unitOfTime.DurationConstructor
    );
    const middleOfYear = new Date(
      (endOfCurrentYear.toDate().getTime() +
        startOfCurrentYear.toDate().getTime()) /
        2
    );

    endOfCurrentYear =
      local === 'fa_IR@calendar=persian'
        ? moment(
            endOfDate(middleOfYear, 'year', true, getLangBaseOnLocal(local))
          )
        : moment(
            endOfDate(middleOfYear, 'year', true, getLangBaseOnLocal(local))
          ).locale('en');
    startOfCurrentYear =
      local === 'fa_IR@calendar=persian'
        ? moment(
            startOfDate(middleOfYear, 'year', true, getLangBaseOnLocal(local))
          )
        : moment(
            startOfDate(middleOfYear, 'year', true, getLangBaseOnLocal(local))
          ).locale('en');
  }
  return dateFilterPicker;
}

const getSelectedMonthIndex = (monthName: string) => {
  switch (monthName) {
    case '1stMonth':
      return 0;
    case '2ndMonth':
      return 1;
    case '3rMonth':
      return 2;
    case '4thMonth':
      return 3;
    case '5thMonth':
      return 4;
    case '6thMonth':
      return 5;
    case '7thMonth':
      return 6;
    case '8thMonth':
      return 7;
    case '9thMonth':
      return 8;
    case '10thMonth':
      return 9;
    case '11thMonth':
      return 10;
    case '12thMonth':
      return 11;
    default:
      return 0;
  }
};

class TasksListContainer extends React.Component<
  IComponentProps,
  IComponentStates
> {
  static contextType = TourContext;

  dateFilterOptions: IOption[];
  monthFilterOptions: IOption[];
  timer: any;
  dropdownOptions: IOption[] = [
    { value: 'all', label: i18n.t('all', { ns: 'taskManager' }) },
    { value: 'complete', label: i18n.t('complete', { ns: 'taskManager' }) },
    { value: 'incomplete', label: i18n.t('incomplete', { ns: 'taskManager' }) },
    { value: 'started', label: i18n.t('started', { ns: 'taskManager' }) },
    { value: 'notStarted', label: i18n.t('notStarted', { ns: 'taskManager' }) },
  ];
  constructor(props: IComponentProps) {
    super(props);
    this.dateFilterOptions = getDateFilterOptions(getCalendarLocal());
    this.monthFilterOptions = [
      { value: '1stMonth', label: i18n.t('1stMonth', { ns: 'survey' }) },
      { value: '2ndMonth', label: i18n.t('2ndMonth', { ns: 'survey' }) },
      { value: '3rdMonth', label: i18n.t('3rdMonth', { ns: 'survey' }) },
      { value: '4thMonth', label: i18n.t('4thMonth', { ns: 'survey' }) },
      { value: '5thMonth', label: i18n.t('5thMonth', { ns: 'survey' }) },
      { value: '6thMonth', label: i18n.t('6thMonth', { ns: 'survey' }) },
      { value: '7thMonth', label: i18n.t('7thMonth', { ns: 'survey' }) },
      { value: '8thMonth', label: i18n.t('8thMonth', { ns: 'survey' }) },
      { value: '9thMonth', label: i18n.t('9thMonth', { ns: 'survey' }) },
      { value: '10thMonth', label: i18n.t('10thMonth', { ns: 'survey' }) },
      { value: '11thMonth', label: i18n.t('11thMonth', { ns: 'survey' }) },
      { value: '12thMonth', label: i18n.t('12thMonth', { ns: 'survey' }) },
    ];

    this.state = {
      from: 0,
      order: 'dueDate',
      loading: true,
      loadingExtraTasks: false,
      loadingCopyTask: false,
      sortType: 'asc',
      taskStatus: 'all',
      showReport: false,
      showCalendarView: false,
      showProgress: false,
      activeProjectId: props.projectId,
      archived: props.archivedDefault || false,
      allTasksFetched: false,
      whichTasks: props.assigneeId ? props.assigneeId : 'all',
      basedOnTemplate: this.getBaseOnTemplate() || 'all',
      fieldValues: {},
      members: undefined,
      selectedAssigneeId: undefined,
      assigneeSelectOpen: false,
      selectedDateFilter: props.startDate
        ? undefined
        : this.dateFilterOptions[1],
      windowWidth: window.innerWidth,
      printLoader: false,
      searchTaskContent: '',
      selectedMonthFilter: '',
      laneLimits: {},
      fetchIdList: [],
      taskCounts: null,
    };
  }
  getBaseOnTemplate = () => {
    if (this.props.templateId) return this.props.templateId;
    else if (this.props.projectId)
      return this.props.projects[this.props.projectId]?.templates?.find(
        (item) => item.allowRoot
      )?.taskId;
    else
      return Object.values(this.props.company?.projects || {}).reduce(
        (acc: number, project) => {
          if (project.archived) return acc;
          !acc &&
            project.templates.map((template) => {
              if (template.allowRoot) acc = template.taskId;
            });
          return acc;
        },
        0
      );
  };

  getLaneLimit = (laneId: string) => {
    return this.state.laneLimits[laneId] ?? 20;
  };

  loadMoreForLane = (laneId: string, boardField: any) => {
    const from = this.getLaneLimit(laneId);
    this.setState(
      (prev) => ({
        laneLimits: {
          ...prev.laneLimits,
          [laneId]: this.getLaneLimit(laneId) + 20,
        },
      }),
      async () => {
        await this.handleLoadTaskBasedOnBoardfieldOption(
          boardField?.name || '',
          laneId,
          from
        );
      }
    );
  };

  showBoard = (boardField?: IField) => {
    this.setState(
      { boardField, from: 0, allTasksFetched: false, loading: true },
      async () => {
        if (!boardField) return;
        const fieldName = boardField.name;
        const options = Object.values(boardField.options || {});
        const CONCURRENCY_LIMIT = 5;

        const pool: Promise<void>[] = [];

        for (const fieldOption of options) {
          const taskPromise = this.handleLoadTaskBasedOnBoardfieldOption(
            fieldName,
            fieldOption
          ).then(() => {
            const index = pool.indexOf(taskPromise);
            if (index > -1) {
              pool.splice(index, 1);
            }
          });

          pool.push(taskPromise);

          if (pool.length >= CONCURRENCY_LIMIT) {
            await Promise.race(pool);
          }
        }
        await Promise.all(pool);

        this.setState({ loading: false });
      }
    );
  };

  handleLoadTaskBasedOnBoardfieldOption = (
    fieldName: string,
    fieldOption: string,
    from: number = 0
  ) => {
    return this.handleRequireTasks(
      'notDeleted',
      undefined,
      fieldName,
      fieldOption,
      false,
      from
    );
  };

  includeCompleted: RefObject<HTMLInputElement> = createRef();
  includeStarted: RefObject<HTMLInputElement> = createRef();
  includeNotStarted: RefObject<HTMLInputElement> = createRef();
  welcomeTour = [
    {
      selector: '.welcome',
      content: `${i18n.t('welcome', { ns: 'general' })}`,
      position: 'center' as StepType['position'],
      category: 'welcome',
      style: {
        zIndex: '99999999999999',
      },
    },
  ];
  componentDidMount = () => {
    this.requireTasksFromScratch();
    this.setCompanyMembers();
    this.fetchTemplateTaskCounts();
    if (window.location.hash === '#mention')
      this.setState({ whichTaskss: 'mentions' });

    // Add window resize listener
    window.addEventListener('resize', this.handleWindowResize);

    // if (
    //   window.location.href.includes('homepage') &&
    //   !this.props.tourFlags.find(
    //     (flag: IUserFlag) => flag.flagName === 'welcome'
    //   )
    // )
    //   (this.context as any).setSteps?.((prevState: any) => [
    //     ...(this.welcomeTour || []),
    //     ...(prevState || []),
    //   ]);
  };

  // recently added
  componentWillUnmount = () => {
    clearTimeout(this.timer);
    window.removeEventListener('resize', this.handleWindowResize);
  };

  handleWindowResize = () => {
    this.setState({ windowWidth: window.innerWidth });
  };

  private requireTasksFromScratch() {
    this.setState({ from: 0, allTasksFetched: false, loading: true }, () => {
      this.handleRequireTasks().then(
        () => {
          /*do nothing*/
        },
        (err: any) => {
          display(err);
        }
      );
    });
  }

  setCompanyMembers = () => {
    // console.log('setting company members');
    const memberIds = this.props?.company?.members
      ? Object.keys(this.props.company?.members || {})
      : [];
    const members: {
      [key: number]: IUserOption | IRoleOption;
    } = memberIds.reduce((list: { [key: number]: IUserOption }, memberId) => {
      const userRoles = this.props.company?.members?.[memberId]
        ?.roles as ICompanyMemberRole;
      list[`USER_${memberId}`] = {
        userId: memberId,
        name: this.props.users?.[memberId]?.name || '',
        optionType: 'user',
        inactive: this.props.company.members?.[memberId]?.inactive,
        roles:
          userRoles && Object.keys(userRoles).length > 0
            ? Object.values(userRoles || {}).map(
                (role: ICompanyMemberRole) => ({
                  name: role.name,
                  id: role.id,
                })
              )
            : [],
      };
      return list;
    }, {});
    this.setState({ members });
  };

  static getDerivedStateFromProps(
    nextProps: IComponentProps,
    prevState: IComponentStates
  ) {
    if (prevState.activeProjectId !== nextProps.projectId) {
      return {
        activeProjectId: nextProps.projectId,
        from: 0,
        allTasksFetched: false,
      };
    }
    return {};
  }

  componentDidUpdate(prevProps: IComponentProps, prevState: IComponentStates) {
    if (
      prevProps.company?.members !== this.props.company?.members ||
      this.props.users !== prevProps.users
    ) {
      this.setCompanyMembers();
    }
    if (
      prevState.basedOnTemplate !== this.state.basedOnTemplate ||
      prevState.selectedDateFilter !== this.state.selectedDateFilter ||
      prevState.selectedMonthFilter !== this.state.selectedMonthFilter ||
      prevState.fieldValues !== this.state.fieldValues
    ) {
      this.fetchTemplateTaskCounts();
    }
    if (
      this.props.assigneeId === undefined &&
      prevProps.assigneeId !== undefined &&
      !isANumber(this.state.basedOnTemplate)
    ) {
      this.setState({ whichTasks: 'all' });
    }
    if (this.props.projectId !== prevProps.projectId) {
      // const order = `${this.state.order}_${this.state.sortType}` as Order;
      // const availableTemplates = this.getTemplateIds(this.props.projectId);
      const state = {
        from: 0,
        allTasksFetched: false,
        loading: true,
      } as IComponentStates;
      state.basedOnTemplate = this.getBaseOnTemplate() || 'all';
      this.setState(state, () => {
        this.handleRequireTasks().then();
      });
    }
    if (this.props.companyId !== prevProps.companyId) {
      const params: ISearchParams = {
        companyId: this.props.companyId,
        archived: this.state.archived,
        order: this.state.order,
        parentId: null,
        fieldValues: { ...this.props.fieldValues, ...this.state.fieldValues },
      };
      appDispatch(searchTasks({ params })).then(
        () => {
          this.setState({ loading: false });
        },
        (err: any) => {
          display(err);
        }
      );
    }
    //
    if (prevState.searchTaskContent !== this.state.searchTaskContent) {
      const state = {
        from: 0,
        allTasksFetched: false,
        loading: true,
      } as IComponentStates;
      this.setState(state, () => {
        this.handleRequireTasks().then();
      });
    }
  }

  handleCopyTaskList = (e: React.MouseEvent) => {
    e.preventDefault();
    this.setState({ loadingCopyTask: true });
    this.handleRequireTasks(undefined, true)
      .then((data) => {
        copyTaskListToClipboard(
          data,
          this.props.users,
          this.props.companyId,
          true,
          true,
          true
        );
      })
      .finally(() => {
        this.setState({ loadingCopyTask: false });
      });
  };

  handleChangeActiveProjectId = (activeProjectId?: number) => {
    this.setState({ activeProjectId });
  };

  handleChangeShowDeleted = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    appDispatch(showDeletedAction({ show: value }));
    this.setState({ from: 0, allTasksFetched: false }, () => {
      this.loadWithDeletedTasks(value ? 'all' : 'notDeleted');
    });
  };
  handleChangeSortType = (order: any) => {
    this.setState({ sortType: order });
    this.setState(
      {
        loading: true,
        from: 0,
        allTasksFetched: false,
      },
      () => {
        this.handleRequireTasks();
      }
    );
  };
  handleChangeOrder = (order: Order) => {
    this.setState({
      order: order,
      sortType:
        order === 'creationDate'
          ? 'desc'
          : order === 'dueDate'
            ? 'asc'
            : order === 'updateDate'
              ? 'desc'
              : order === 'startDate'
                ? 'asc'
                : this.state.sortType,
    });

    this.setState(
      {
        loading: true,
        from: 0,
        allTasksFetched: false,
      },
      () => {
        this.handleRequireTasks();
      }
    );
  };
  handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target;
    const value =
      target.type === 'checkbox'
        ? (target as HTMLInputElement).checked
        : target.value;
    this.setState({
      [target.name]: value,
    });
    if (target.name === 'archived') {
      this.setState({ from: 0, allTasksFetched: false, loading: true }, () => {
        this.handleRequireTasks();
      });
    } else if (target.name === 'order') {
      this.setState(
        {
          loading: true,
          from: 0,
          allTasksFetched: false,
        },
        () => {
          this.handleRequireTasks();
        }
      );
    } else if (target.name === 'sortType') {
      this.setState(
        {
          loading: true,
          from: 0,
          allTasksFetched: false,
        },
        () => {
          this.handleRequireTasks();
        }
      );
    } else if (target.name === 'whichTasks') {
      this.setState(
        {
          loading: true,
          from: 0,
          allTasksFetched: false,
        },
        () => {
          this.handleRequireTasks();
        }
      );
    } else if (target.name === 'taskStatus') {
      this.setState(
        {
          loading: true,
          from: 0,
          allTasksFetched: false,
        },
        () => {
          this.handleRequireTasks();
        }
      );
    }
  };

  handleChangeTemplate =
    (templateId: 'all' | number, fieldValues: { [key: string]: string }) =>
    () => {
      // templateId could be template ID or 'all' | 'categorized'
      // Clone fieldValues so filter changes get a new reference (TemplateSelectorList mutates in place)
      this.setState(
        {
          loading: true,
          from: 0,
          allTasksFetched: false,
          basedOnTemplate: templateId,
          fieldValues: { ...fieldValues },
          boardField:
            this.state.basedOnTemplate === templateId
              ? this.state.boardField
              : undefined,
        },
        () => {
          this.handleRequireTasks();
        }
      );
    };

  loadTasks = () => {
    return this.handleRequireTasks();
  };

  loadExtraTasks = () => {
    if (this.state.from === 0) return Promise.resolve();
    return this.handleRequireTasks();
  };

  loadWithDeletedTasks = (deleteState?: DeleteState) => {
    return this.handleRequireTasks(deleteState);
  };
  getTaskPromise: Promise<any> | null = null;
  finalCountVar = 0;
  fetchCounter = 0;
  getFilteredMonthByADate = (date: number, targetMonthIndex: number) => {
    const monthFormat = i18n.language === 'en' ? 'M' : 'jM';
    if (Number(moment(date).format(monthFormat)) === targetMonthIndex)
      return true;
    else return false;
  };
  getFilteredMonthByFromDueDate = (
    fromDate: number,
    dueDate: number,
    targetMonthIndex: number
  ) => {
    const monthFormat = i18n.language === 'en' ? 'M' : 'jM';
    if (
      Number(moment(fromDate).format(monthFormat)) <= targetMonthIndex &&
      Number(moment(dueDate).format(monthFormat)) >= targetMonthIndex
    )
      return true;
    else return false;
  };

  handleSelectedMonth = () => {
    const { selectedMonthFilter, selectedDateFilter } = this.state;
    const yearFormat = i18n.language === 'en' ? 'YYYY' : 'jYYYY';
    const local = i18n.language === 'en' ? 'en' : 'fa_IR@calendar=persian';
    const fromMonthIndex = jMonthIndex(
      Number(
        englishNumber(moment(selectedDateFilter?.startDate).format(yearFormat))
      ),
      getSelectedMonthIndex(selectedMonthFilter as string)
    );
    const toMonthIndex = fromMonthIndex + 1;
    const fromTimestamp = monthIndexToTimestamp(fromMonthIndex, local);
    const toTimestamp = monthIndexToTimestamp(toMonthIndex, local) - 1;
    return [fromTimestamp, toTimestamp];
  };

  taskCountsRequestId = 0;

  getReportDateRange = (): { startDate: number; toDate: number } => {
    const { selectedDateFilter, selectedMonthFilter } = this.state;
    if (selectedMonthFilter && selectedDateFilter?.startDate) {
      const [fromTimestamp, toTimestamp] = this.handleSelectedMonth();
      return { startDate: fromTimestamp, toDate: toTimestamp };
    }
    if (selectedDateFilter?.startDate) {
      const startDate = selectedDateFilter.startDate;
      const toDate =
        selectedDateFilter.endDate ??
        moment(startDate).add(1, 'year').valueOf() - 1;
      return { startDate, toDate };
    }
    const now = Date.now();
    const fiftyYearsMs = 50 * 365.25 * 24 * 60 * 60 * 1000;
    return {
      startDate: now - fiftyYearsMs,
      toDate: now + fiftyYearsMs,
    };
  };

  fetchTemplateTaskCounts = () => {
    const { basedOnTemplate, fieldValues } = this.state;
    const { companyId, fieldValues: propFieldValues, tasks } = this.props;
    if (basedOnTemplate === 'all' || typeof basedOnTemplate !== 'number') {
      this.setState({ taskCounts: null });
      return;
    }

    const template = tasks[basedOnTemplate];
    if (!template?.taskProgressType?.main) {
      this.setState({ taskCounts: null });
      return;
    }

    const requestId = ++this.taskCountsRequestId;
    const { startDate, toDate } = this.getReportDateRange();
    const templateReport: ITemplateReportMark = {
      completedTaskCountMain: true,
      inProgressTaskCountMain: true,
      notStartedTaskCountMain: true,
      averageProgressMain: false,
      completedTaskCountSecondary: false,
      inProgressTaskCountSecondary: false,
      notStartedTaskCountSecondary: false,
      averageProgressSecondary: false,
      completedTaskCountVerifier: false,
      rejectedTaskCountVerifier: false,
      notStartedTaskCountVerifier: false,
      completedAssignmentCount: false,
      inProgressAssignmentCount: false,
      notStartedAssignmentCount: false,
      averageAssignmentProgress: false,
    };

    appDispatch(
      getTaskReport({
        companyId,
        templateReport,
        startDate,
        toDate,
        projectId: null,
        parentId: null,
        templateId: basedOnTemplate,
        assigneeId: null,
        splitMonths: 1,
        splitField: '__time',
        fieldValues: { ...propFieldValues, ...fieldValues },
      })
    )
      .then((result: { data: { total?: Partial<ITemplateTaskCounts> } }) => {
        if (requestId !== this.taskCountsRequestId) return;
        const totalData = result?.data?.total || {};
        const completedTaskCountMain = totalData.completedTaskCountMain || 0;
        const inProgressTaskCountMain = totalData.inProgressTaskCountMain || 0;
        const notStartedTaskCountMain = totalData.notStartedTaskCountMain || 0;
        this.setState({
          taskCounts: {
            completedTaskCountMain,
            inProgressTaskCountMain,
            notStartedTaskCountMain,
            total:
              completedTaskCountMain +
              inProgressTaskCountMain +
              notStartedTaskCountMain,
          },
        });
      })
      .catch(() => {
        if (requestId !== this.taskCountsRequestId) return;
        this.setState({ taskCounts: null });
      });
  };

  handleRequireTasks = async (
    deleteState?: DeleteState,
    copyTask?: boolean,
    laneKey: string = '',
    laneValue: string = '',
    fetchAll: boolean = true,
    laneFrom: number = 0
  ) => {
    const {
      endDate,
      parentId,
      thisUser,
      companyId,
      projectId,
      assigneeId,
      startDate,
      reportParams,
      providerItemId,
      showDeleted,
      includeDrafted,
      ancestorId,
    } = this.props;

    const {
      calendarStartDate,
      calendarEndDate,
      selectedAssigneeId,
      selectedDateFilter,
      archived,
      order,
      from,
      whichTasks,
      basedOnTemplate,
      taskStatus,
      fieldValues,
      sortType,
      searchTaskContent,
      selectedMonthFilter,
    } = this.state;

    const taskOrder = `${order}_${sortType}` as Order;

    let fromTimestamp = null;
    let toTimestamp = null;
    if (selectedMonthFilter) {
      [fromTimestamp, toTimestamp] = this.handleSelectedMonth();
    }

    const finalCount = copyTask
      ? 1000
      : calendarStartDate
        ? 100
        : this.state.boardField || window.innerHeight > largeScreenSize
          ? largePageSize
          : pageSize;
    this.finalCountVar = finalCount;
    const finalFrom = copyTask ? 0 : from; /*+ finalCount*/
    if (finalFrom > 0) this.setState({ loadingExtraTasks: true });
    if (!copyTask) this.setState({ loading: true });
    const params: ISearchParams = {
      from: finalFrom,
      count: finalCount,
      order: taskOrder,
      toDate: toTimestamp
        ? toTimestamp
        : calendarEndDate?.getTime() || endDate || selectedDateFilter?.endDate,
      archived: archived ? undefined : archived,
      fromDate: fromTimestamp
        ? fromTimestamp
        : calendarStartDate?.getTime() ||
          startDate ||
          selectedDateFilter?.startDate,
      parentId: parentId ? parentId : -1,
      companyId,
      projectId,
      itemId: providerItemId,
      assigneeId: assigneeId || selectedAssigneeId,
      includeDrafted: includeDrafted,
      deleteState: deleteState || (showDeleted ? 'all' : 'notDeleted'),
      text: searchTaskContent,
    };
    if (whichTasks === 'mentions') {
      params.mentioned = true;
    }
    if (whichTasks === 'assignedToMe') {
      params.assigneeId = Number(thisUser.userId);
    } else if (whichTasks === 'createdByMe') {
      params.creatorIds = Number(thisUser.userId);
    }
    if (basedOnTemplate !== 'all') {
      params.templateId = basedOnTemplate;
    }
    if (taskStatus !== 'all') {
      if (whichTasks === 'assignedToMe') {
        params.status = taskStatus;
      } else {
        switch (taskStatus) {
          case 'incomplete':
            params.mainProgressFrom = 0;
            params.mainProgressTo = 99.99;
            break;
          case 'notStarted':
            params.mainProgressFrom = 0;
            params.mainProgressTo = 0.99;
            break;
          case 'started':
            params.mainProgressFrom = 1;
            params.mainProgressTo = 99.99;
            break;
          case 'rejected':
            params.mainProgressFrom = -1;
            params.mainProgressTo = -0.01;
            break;
          case 'complete':
            params.mainProgressFrom = 100;
            params.mainProgressTo = 100;
            break;
        }
      }
    }
    if (reportParams) {
      params.verifyStatus = reportParams.verifyStatus;
      params.mainProgressFrom = reportParams.mainProgressFrom;
      params.mainProgressTo = reportParams.mainProgressTo;
      params.secondaryProgressFrom = reportParams.secondaryProgressFrom;
      params.secondaryProgressTo = reportParams.secondaryProgressTo;
      params.status = reportParams.status;
    }

    params.fieldValues = {
      ...fieldValues,
      ...(this.props.fieldValues || {}),
    };

    if (laneKey) {
      params.from = laneFrom;
      params.count = 20;
      if (laneValue)
        params.fieldValues = { ...params.fieldValues, [laneKey]: laneValue };
      else
        params.excludeFields = {
          [laneKey]: Object.values(this.state.boardField?.options || {}),
        };
    }

    if (ancestorId) {
      params.fieldValues = {
        ...params.fieldValues,
        __ancestor: ancestorId.toString(),
      };
    }
    try {
      const currentCount = ++this.fetchCounter;
      if (!copyTask || fetchAll) this.setState({ loading: true });
      const { tasks: data } = await appDispatch(searchTasks({ params }));
      if (!copyTask) {
        this.setState(
          {
            loadingExtraTasks: data.length === finalCount,
            from: finalFrom + data.length,
          },
          () => {
            if (currentCount === this.fetchCounter && !copyTask) {
              if (
                fetchAll &&
                data.length === finalCount &&
                (this.state.showCalendarView || this.state.boardField)
              )
                // continue to fetch all tasks
                this.handleRequireTasks(deleteState);
              else {
                this.setState({ loading: false });
              }
            } else {
              /*another action requested a new taskFetch and this call is ignored f*/
            }
          }
        );
        if (data.length === 0) {
          this.setState({ allTasksFetched: true });
        }
      }
      return Promise.resolve(data);
    } catch (err) {
      this.setState({ loadingExtraTasks: false });
      display(err);
      return Promise.reject(err);
    }
  };

  handleUpdateAssignmentTask = (assignment: IAssignmentView) => {
    appDispatch(markTaskAssignment({ assignment })).catch((err: any) => {
      display(err);
    });
  };

  handleSetCalendarDateRange = (start: Date, end: Date) => {
    this.setState({ calendarStartDate: start, calendarEndDate: end, from: 0 });
    this.loadTasks();
  };

  handleUpdateTask = (task: ITask) => {
    appDispatch(updateTask({ task })).catch((err: any) => {
      display(err);
    });
  };

  handleChangeWhichTask = (whichTasks: any) => {
    let newBasedOnTemplate = this.state.basedOnTemplate;
    let newOrder = this.state.order;
    let newSortType = this.state.sortType;
    const status = 'all';
    // if (whichTasks === 'all' && !isANumber(newBasedOnTemplate)) {
    //   newBasedOnTemplate = this.getBaseOnTemplate();
    // }
    if (whichTasks !== 'all' && !isANumber(newBasedOnTemplate)) {
      newBasedOnTemplate = 'all';
    }
    let { selectedDateFilter } = this.state;
    if (whichTasks === 'mentions') {
      selectedDateFilter = undefined;
    }
    if (whichTasks === 'assignedToMe') {
      newOrder = 'dueDate';
      newSortType = 'asc';
    }
    if (whichTasks === 'createdByMe') {
      newOrder = 'creationDate';
      newSortType = 'desc';
    }
    const newFieldValues =
      newBasedOnTemplate === this.state.basedOnTemplate
        ? this.state.fieldValues
        : {};
    this.setState(
      {
        loading: true,
        from: 0,
        allTasksFetched: false,
        whichTasks,
        basedOnTemplate: newBasedOnTemplate,
        fieldValues: newFieldValues,
        selectedDateFilter,
        order: newOrder,
        sortType: newSortType,
        taskStatus: status as TaskStatus,
      },
      () => {
        this.handleRequireTasks();
      }
    );
  };

  handleChangeView = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    this.setState((state) => ({
      showReport: !state.showReport,
    }));
  };

  handleShowProgress = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    this.setState((state) => ({
      showProgress: !state.showProgress,
    }));
  };

  handleShowCalendar = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    const showCalendarView = !this.state.showCalendarView;
    const lang = (
      this.props.local ? this.props.local : getLanguageBaseOnTZ()
    ) as ILocal;
    this.setState(
      {
        from: 0,
        showCalendarView: showCalendarView,
        calendarStartDate: showCalendarView
          ? startOfDate(new Date(), 'month', false, getLangBaseOnLocal(lang))
          : undefined,
        calendarEndDate: showCalendarView
          ? endOfDate(new Date(), 'month', false, getLangBaseOnLocal(lang))
          : undefined,
      },
      () => {
        if (!this.state.showCalendarView) {
          this.loadTasks();
        }
      }
    );
  };

  dfs = (compTemp: any, projTemp: number[]): number[] => {
    const stack: number[] = [...projTemp];
    stack.forEach((tempId) => {
      compTemp[tempId]?.children?.forEach((childTemp: any) => {
        if (!stack.includes(childTemp)) {
          stack.push(childTemp);
        }
      });
    });
    if (stack.length === projTemp.length) {
      return stack;
    }
    return this.dfs(compTemp, stack);
  };

  handlePrintTasksList = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    const {
      endDate,
      parentId,
      companyId,
      projectId,
      startDate,
      reportParams,
      providerItemId,
      fieldValues: fieldValuesFromProps,
    } = this.props;
    const {
      order,
      sortType,
      archived,
      taskStatus,
      whichTasks,
      basedOnTemplate,
      fieldValues: fieldValuesFromState,
      selectedDateFilter,
      selectedAssigneeId,
      selectedMonthFilter,
    } = this.state;
    let params = `includeDrafted=${this.props.includeDrafted}&projectId=${projectId}&archived=${archived}&order=${order}_${sortType}&whichTasks=${whichTasks}&basedOnTemplate=${basedOnTemplate}&taskStatus=${taskStatus}`;
    if (
      taskStatus !== 'all' &&
      !selectedAssigneeId &&
      whichTasks !== 'assignedToMe'
    ) {
      let minProgress = 0,
        maxProgress = 100;
      switch (taskStatus) {
        case 'complete':
          minProgress = 100;
          break;
        case 'incomplete':
          maxProgress = 99.99;
          break;
        case 'notStarted':
          maxProgress = 0;
          break;
        case 'started':
          minProgress = 0.01;
          break;
        case 'rejected':
          maxProgress = -1;
          minProgress = -1;
          break;
      }
      params = `${params}&mainProgressFrom=${minProgress}&mainProgressTo=${maxProgress}`;
    }
    let fromTimestamp = null;
    let toTimestamp = null;
    if (selectedMonthFilter) {
      [fromTimestamp, toTimestamp] = this.handleSelectedMonth();
    }
    if (startDate || selectedDateFilter?.startDate || fromTimestamp) {
      params = `${params}&fromDate=${
        fromTimestamp
          ? fromTimestamp
          : Math.max(selectedDateFilter?.startDate || 0, startDate || 0)
      }`;
    }
    if (endDate || selectedDateFilter?.endDate || toTimestamp) {
      params = `${params}&toDate=${
        toTimestamp
          ? toTimestamp
          : Math.min(
              selectedDateFilter?.endDate || +Infinity,
              endDate || +Infinity
            )
      }`;
    }
    if (parentId) {
      params = `${params}&parentId=${parentId}`;
    }
    if (providerItemId) {
      params = `${params}&itemId=${providerItemId}`;
    }
    if (reportParams) {
      if (typeof reportParams.mainProgressFrom === 'number') {
        params = `${params}&mainProgressFrom=${reportParams.mainProgressFrom}&mainProgressTo=${reportParams.mainProgressTo}`;
      } else if (typeof reportParams.secondaryProgressFrom === 'number') {
        params = `${params}&secondaryProgressFrom=${reportParams.secondaryProgressFrom}&secondaryProgressTo=${reportParams.secondaryProgressTo}`;
      } else if (reportParams.verifyStatus) {
        params = `${params}&verifyStatus=${reportParams.verifyStatus}`;
      } else if (reportParams.status) {
        params = `${params}&status=${reportParams.status}`;
      }
    }

    if (this.props.assigneeId || this.state.selectedAssigneeId) {
      params = `${params}&assigneeId=${this.state.selectedAssigneeId || this.props.assigneeId}`;
    }

    const finalFieldValues = {
      ...fieldValuesFromProps,
      ...fieldValuesFromState,
    };
    if (Object.keys(finalFieldValues).length) {
      params = `${params}&fieldValues=${JSON.stringify(finalFieldValues)}`;
    }
    window.open(`/t/${companyId}/print/tasks?${params}`);
  };

  handleChangeAssignee = (values: Array<IUserOption | IRoleOption>) => {
    const users = values.filter((e) => {
      return e.optionType === 'user';
    });
    let state = {
      assigneeSelectOpen: false,
      loading: true,
      from: 0,
      allTasksFetched: false,
    } as IComponentStates;
    if (users.length) {
      state = {
        ...state,
        selectedAssigneeId: (users[0] as IUserOption).userId,
      };
    } else {
      state = { ...state, selectedAssigneeId: undefined };
    }
    this.setState(state, () => {
      this.handleRequireTasks();
    });
  };

  getTemplateIds = (projectId?: number) => {
    let templatesIds: number[] = [];
    const { company, tasks } = this.props;
    const companyTemplates = company?.templates;
    if (projectId) {
      const projectTemplatesIds =
        this.props.projects[projectId]?.templates || [];
      templatesIds = companyTemplates
        ? this.dfs(
            companyTemplates,
            projectTemplatesIds
              .map((v: any) => (v.allowRoot ? v.taskId : 0))
              .filter((it: any) => it !== 0)
          )
        : [];
    } else if (companyTemplates) {
      const allProjectRootTemplateIds = [] as any[];
      Object.values(this.props.projects || {}).forEach((it) => {
        if (it.companyId !== this.props.companyId) return;
        if (it.archived && !this.state.archived) return;
        it?.templates
          ?.map((v) => (v.allowRoot ? v.taskId : 0))
          .filter((it: any) => it !== 0)
          .forEach((template: any) => {
            allProjectRootTemplateIds.push(template);
          });
      });
      templatesIds = Object.keys(companyTemplates).map((it) => Number(it));
      templatesIds = this.dfs(
        companyTemplates,
        removeDuplicates(allProjectRootTemplateIds)
      );
    }
    templatesIds = templatesIds.filter((id) => {
      return !tasks[id]?.archived;
    });
    return templatesIds.reduce(
      (
        temps: Array<{
          id: number;
          title: string;
          fields: ITask['fields'];
        }>,
        id
      ) => {
        temps.push({
          id,
          title: tasks[id]?.taskName,
          fields: tasks[id]?.fields,
        });
        return temps;
      },
      []
    );
  };

  handlePrintTaskWithProgress = () => {
    const {
      endDate,
      parentId,
      companyId,
      projectId,
      startDate,
      thisUser,
      providerItemId,
      assigneeId,
      includeDrafted,
    } = this.props;
    const {
      order,
      archived,
      taskStatus,
      whichTasks,
      basedOnTemplate,
      calendarStartDate,
      calendarEndDate,
      selectedDateFilter,
      selectedAssigneeId,
      selectedMonthFilter,
    } = this.state;

    let fromTimestamp = null;
    let toTimestamp = null;
    if (selectedMonthFilter) {
      [fromTimestamp, toTimestamp] = this.handleSelectedMonth();
    }

    const params: ISearchParams = {
      from: 0,
      count: 1000,
      order,
      toDate: toTimestamp
        ? toTimestamp
        : calendarEndDate?.getTime() || endDate || selectedDateFilter?.endDate,
      archived: archived ? undefined : archived,
      fromDate: fromTimestamp
        ? fromTimestamp
        : calendarStartDate?.getTime() ||
          startDate ||
          selectedDateFilter?.startDate,
      parentId: parentId ? parentId : -1,
      companyId,
      projectId,
      itemId: providerItemId,
      assigneeId: assigneeId || selectedAssigneeId,
      includeDrafted: includeDrafted,
      printable: true,
    };
    this.setState({ printLoader: true });
    if (whichTasks === 'mentions') {
      params.mentioned = true;
    }
    if (whichTasks === 'assignedToMe') {
      params.assigneeId = Number(thisUser.userId);
    } else if (whichTasks === 'createdByMe') {
      params.creatorIds = Number(thisUser.userId);
    }
    if (basedOnTemplate !== 'all') {
      params.templateId = basedOnTemplate;
    }
    if (taskStatus !== 'all') {
      if (whichTasks === 'assignedToMe') {
        params.status = taskStatus;
      } else {
        switch (taskStatus) {
          case 'incomplete':
            params.mainProgressFrom = 0;
            params.mainProgressTo = 99.99;
            break;
          case 'notStarted':
            params.mainProgressFrom = 0;
            params.mainProgressTo = 0.99;
            break;
          case 'started':
            params.mainProgressFrom = 1;
            params.mainProgressTo = 99.99;
            break;
          case 'rejected':
            params.mainProgressFrom = -1;
            params.mainProgressTo = -0.01;
            break;
          case 'complete':
            params.mainProgressFrom = 100;
            params.mainProgressTo = 100;
            break;
        }
      }
    }
    appDispatch(searchTasks({ params })).then(
      (data: any) => {
        const queryParamTaskIds: string = data.tasks.reduce(
          (acc: string, currentValue: ITask) => {
            acc += `${currentValue.id},`;
            return acc;
          },
          ''
        );
        window.open(`/t/${companyId}/tasks/print/1/${queryParamTaskIds}`);
        this.setState({ loading: false, printLoader: false });
      },
      (err: any) => {
        display(err);
      }
    );
  };

  onSearchTaskHandler = (e: any) => {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    if (e.target.value.length >= 3) {
      this.timer = setTimeout(() => {
        this.setState({ searchTaskContent: e.target.value as string });
      }, 3000);
    } else {
      this.setState({ searchTaskContent: '' as string });
    }
  };

  handleSelectValue = (value: string): IOption[] => {
    let label = '';
    if (value === 'incomplete') {
      label = i18n.t('incomplete', { ns: 'taskManager' });
    } else if (value === 'completed') {
      label = i18n.t('complete', { ns: 'taskManager' });
    } else if (value === 'started') {
      label = i18n.t('started', { ns: 'taskManager' });
    } else if (value === 'notStarted') {
      label = i18n.t('notStarted', { ns: 'taskManager' });
    } else {
      label = i18n.t('all', { ns: 'taskManager' });
    }
    return [{ value, label }];
  };

  handleOnChangeSelectValue = (values: IOption[]) => {
    this.setState(
      {
        taskStatus: values[0].value as TaskStatus,
        loading: true,
        from: 0,
        allTasksFetched: false,
      },
      () => {
        this.handleRequireTasks();
      }
    );
  };

  handleLaneButtonClick = (
    templateId: number,
    board: any,
    selectedFieldTitle: string
  ) => {
    if (
      this.props.dirtySet.length > 0 &&
      !confirm(i18n.t('changesWillBeLost', { ns: 'taskManager' }))
    ) {
      return;
    }

    this.props.onOpenTaskEditor('new', templateId, board, selectedFieldTitle);
  };

  CustomLaneHeader = (
    { title }: any,
    basedOnTemplate: any,
    boardField: any
  ) => {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px',
          color: black,
        }}
      >
        <span style={{ fontWeight: 'bold' }}>{title}</span>
        <div>
          {/* <button onClick={() => this.loadMoreForLane(lane.id, boardField)}>
            load more
          </button> */}
          <Button
            color="black-outline"
            style={{
              padding: '0px 6.5px',
              fontSize: '1.2rem',
              borderRadius: '10rem',
            }}
            onClick={() =>
              this.handleLaneButtonClick(basedOnTemplate, boardField, title)
            }
          >
            +
          </Button>
        </div>
      </div>
    );
  };

  render() {
    const {
      tasks,
      endDate,
      parentId,
      ancestorId,
      thisUser,
      company,
      companyId,
      startDate,
      projectId,
      projects,
      reportParams,
      taskSelected,
      companyOwnerId,
      onOpenTaskEditor,
      withFewerOptions,
      providerItemId,
      fieldValues: propFieldValues,
      showDeleted,
      assigneeId,
      includeDrafted,
      isModal = false,
      // tourFlags,
    } = this.props;
    const {
      from,
      order,
      loading,
      archived,
      sortType,
      loadingExtraTasks,
      showReport,
      taskStatus,
      whichTasks,
      showProgress,
      showCalendarView,
      allTasksFetched,
      basedOnTemplate,
      fieldValues,
      selectedAssigneeId,
      members,
      boardField,
      assigneeSelectOpen,
      selectedDateFilter,
      searchTaskContent,
      selectedMonthFilter,
      taskCounts,
    } = this.state;
    const finalAssigneeId =
      whichTasks === 'assignedToMe'
        ? undefined
        : assigneeId || selectedAssigneeId;
    const userId = Number(thisUser.userId);
    const templates = this.getTemplateIds(projectId);

    // filter tasks based on their company
    let filteredTasks = Object.values(tasks).filter(
      (task) => company?.projects?.[task.projectId]
    );

    if (includeDrafted !== undefined) {
      filteredTasks = filteredTasks.filter((task) => {
        return includeDrafted || !task.draft;
      });
    }
    // filter tasks based on archived, and not be a template
    filteredTasks = filteredTasks.filter(
      (task) => !task.template && (archived || task.archived === archived)
    );

    // filter tasks based on their project
    if (projectId) {
      filteredTasks = filteredTasks.filter(
        (task) => task.projectId === projectId
      );
    }

    // filter tasks based on their parentId
    if (parentId) {
      filteredTasks = filteredTasks.filter(
        (task) => task.parentId === parentId
      );
    }
    if (ancestorId) {
      filteredTasks = filteredTasks.filter((task) =>
        task.ancestors?.includes(ancestorId)
      );
    }

    // filter tasks based on delete state
    if (!showDeleted)
      filteredTasks = filteredTasks.filter((task) => !task.deleteState);

    // filter tasks based on their creator
    if (whichTasks === 'createdByMe') {
      filteredTasks = filteredTasks.filter((task) => task.creator === userId);
    }
    // filter tasks based on mention
    if (whichTasks === 'mentions') {
      filteredTasks = filteredTasks.filter((task) => task.mentions.length > 0);
    }
    // filter tasks based on their assignment and verifier
    if (whichTasks === 'assignedToMe') {
      filteredTasks = filteredTasks.filter(
        (task) =>
          Object.keys(task.assignee || {}).includes(userId?.toString()) ||
          (task.verifiers && task.verifiers[userId]) ||
          task.taskPublisher?.userId === userId ||
          task.subTaskPublisher?.userId === userId
      );
    }

    // filter tasks based on their template
    if (basedOnTemplate !== 'all') {
      filteredTasks = filteredTasks.filter(
        (task) => task.templateId === basedOnTemplate
      );
    }
    // filter tasks based on their status
    if (taskStatus !== 'all') {
      if (whichTasks === 'assignedToMe') {
        filteredTasks = filteredTasks.filter((task) => {
          if (taskStatus === 'incomplete') {
            return task.assignee[userId]?.status !== 'complete';
          }
          return task.assignee[userId]?.status === taskStatus;
        });
      } else {
        filteredTasks = filteredTasks.filter((task) => {
          const progress = task.mainProgress;
          switch (taskStatus) {
            case 'complete':
              return progress === 100;
            case 'incomplete':
              return progress! < 100 && progress! >= 0;
            case 'notStarted':
              return (
                progress === 0 || progress === undefined || progress === null
              );
            case 'started':
              return progress! < 100 && progress! > 0;
            case 'rejected':
              return progress! < 0;
            default:
              return true;
          }
        });
      }
    }
    // filter tasks based on start and end date from props and state
    if ((startDate && endDate) || selectedDateFilter) {
      filteredTasks = filteredTasks.filter(
        (task) =>
          (!startDate || task.effectiveDate! >= startDate) &&
          (!endDate || task.effectiveDate! <= endDate) &&
          (!selectedDateFilter?.startDate ||
            task.effectiveDate! >= selectedDateFilter.startDate) &&
          (!selectedDateFilter?.endDate ||
            task.effectiveDate! <= selectedDateFilter.endDate)
      );
    }
    // filter tasks base on filteredMonth
    if (selectedMonthFilter) {
      let fromTimestamp: number | null = null;
      let toTimestamp: number | null = null;
      if (selectedMonthFilter && selectedMonthFilter !== '') {
        [fromTimestamp, toTimestamp] = this.handleSelectedMonth();
      }
      filteredTasks = filteredTasks.filter((task) => {
        if (task.effectiveDate && fromTimestamp && toTimestamp) {
          if (
            task.effectiveDate! >= fromTimestamp &&
            task.effectiveDate! <= toTimestamp
          ) {
            return true;
          } else return false;
        } else return true;
      });
    }
    // filter tasks based on task status and reportParams for task report
    if (reportParams) {
      if (reportParams.mainProgressFrom === 100) {
        filteredTasks = filteredTasks.filter(
          (task) => task.mainProgress === 100
        );
      } else if (reportParams.mainProgressFrom === 0.01) {
        filteredTasks = filteredTasks.filter(
          (task) => task.mainProgress! < 100 && task.mainProgress! > 0
        );
      } else if (reportParams.mainProgressFrom === 0) {
        filteredTasks = filteredTasks.filter((task) => task.mainProgress === 0);
      } else if (reportParams.secondaryProgressFrom === 100) {
        filteredTasks = filteredTasks.filter(
          (task) => task.secondaryProgress === 100
        );
      } else if (reportParams.secondaryProgressFrom === 0.01) {
        filteredTasks = filteredTasks.filter(
          (task) => task.secondaryProgress! < 100 && task.secondaryProgress! > 0
        );
      } else if (reportParams.secondaryProgressFrom === 0) {
        filteredTasks = filteredTasks.filter(
          (task) => task.secondaryProgress === 0
        );
      } else if (reportParams.verifyStatus === 'complete') {
        filteredTasks = filteredTasks.filter((task: ITask) =>
          Object.values(task.verifiers || {}).some(
            (verifier) => verifier.status === 'complete'
          )
        );
      } else if (reportParams.verifyStatus === 'notStarted') {
        filteredTasks = filteredTasks.filter((task: ITask) =>
          Object.values(task.verifiers || {}).some(
            (verifier) => verifier.status === 'notStarted'
          )
        );
      } else if (reportParams.verifyStatus === 'rejected') {
        filteredTasks = filteredTasks.filter((task: ITask) =>
          Object.values(task.verifiers || {}).some(
            (verifier) => verifier.status === 'rejected'
          )
        );
      } else if (reportParams.status) {
        filteredTasks = filteredTasks.filter((task) => {
          return Object.values(task.assignee).find(
            (assignee: IAssignmentView) =>
              assignee.userId === this.props.assigneeId &&
              assignee.status === reportParams.status
          );
        });
      }
    }
    //filter based on assignee
    if (finalAssigneeId) {
      filteredTasks = filteredTasks.filter((task) => {
        return Object.values(task.assignee).find(
          (assignee: IAssignmentView) =>
            Number(assignee.userId) === Number(finalAssigneeId)
        );
      });
    }

    // filter task based on itemId for show in ContentTasks
    if (providerItemId) {
      filteredTasks = filteredTasks.filter((task) => {
        return (
          task.relatedProviderItems &&
          !!task.relatedProviderItems.find((it) => it.id === providerItemId)
        );
      });
    }

    // sort filteredTasks
    filteredTasks = filteredTasks.sort((a, b) => {
      let aDate = a[order];
      let bDate = b[order];
      if (aDate === undefined) {
        aDate = 77777777777000;
      }
      if (bDate === undefined) {
        bDate = 77777777777000;
      }
      if (aDate === bDate) {
        return b.id - a.id;
      }
      if (sortType === 'desc') {
        return bDate - aDate;
      }
      return aDate - bDate;
    });
    // filter task based on field values
    const allFieldValues = { ...fieldValues, ...(propFieldValues || {}) };
    if (Object.keys(allFieldValues).length > 0) {
      filteredTasks = filteredTasks.filter((task) => {
        let res = true;
        const fields = task.fields;
        Object.entries(allFieldValues).forEach((e: string[]) => {
          const k = e[0];
          const v = e[1];
          if (
            !fields.find(
              (f) =>
                f.name === k &&
                ((f.type !== 'multiSelect' &&
                  f.value?.substring(0, 100) === v?.substring(0, 100)) ||
                  (f.type === 'multiSelect' &&
                    f.value
                      ?.split(',')
                      .find(
                        (s: string) =>
                          s?.substring(0, 100) === v?.substring(0, 100)
                      )) ||
                  (v === 'null' && f.value === null))
            )
          )
            res = false;
        });
        return res;
      });
    }

    if (searchTaskContent !== '') {
      filteredTasks = filteredTasks.filter((task) => {
        return preprocessForSearch(task.title).includes(
          preprocessForSearch(searchTaskContent)
        );
      });
    }

    if (!showCalendarView && !boardField)
      filteredTasks = filteredTasks.slice(0, from + this.finalCountVar);

    const iconClassName = !showReport
      ? 'myblack-text'
      : 'mygray-text disable-pointer-events';
    const directMentions = company?.mentionCount?.directAddress || 0;
    const totalMentions = directMentions + (company?.mentionCount?.info || 0);
    const isSupportOrCompanyOwner = isCompanyOwnerOrSupport(thisUser, company);
    const printTypeTemplate = templates
      .filter((template) => tasks[template.id]?.printTemplate !== null)
      .reduce((acc: number[], filteredTemplate) => {
        acc.push(filteredTemplate.id);
        return acc;
      }, []);
    const selectedTemplateName =
      typeof basedOnTemplate === 'number' &&
      templates.find((template) => template.id === basedOnTemplate)?.title;
    // const showTour = !tourFlags.find(
    //   (flag: IUserFlag) => flag.flagName === 'welcome'
    // );

    return (
      <Container halve={false}>
        <div
          style={{
            margin: this.state.windowWidth > 600 ? '0 1rem' : '0 3rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <h3 className="text-2xl">
            {projectId
              ? projects[projectId]?.name || ''
              : (i18n.t('allTasks', { ns: 'taskManager' }) as string)}
          </h3>
          <span>
            {projectId && projects[projectId]?.description && (
              <APopover
                arrow={true}
                curveBorder={true}
                trigger="click"
                target={
                  <ActionLink color={'blue'}>
                    <Icon type="info" style={{ padding: '0rem 0.3rem' }} />
                  </ActionLink>
                }
              >
                <div
                  style={{
                    maxWidth: '500px',
                    whiteSpace: 'pre-wrap',

                    maxHeight: '90vh',
                    overflow: 'scroll',
                  }}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: projects[projectId]?.description,
                    }}
                  />
                </div>
              </APopover>
            )}
          </span>
        </div>
        <div>
          <Header>
            {!withFewerOptions && (
              <Button
                style={{ float: 'right', margin: '3px' }}
                onClick={() => {
                  if (
                    this.props.dirtySet.length > 0 &&
                    !confirm(i18n.t('changesWillBeLost', { ns: 'taskManager' }))
                  ) {
                    return;
                  }
                  this.props.onOpenTaskEditor('new');
                }}
                color="green"
                disabled={
                  !!(
                    projectId &&
                    ((!isSupportOrCompanyOwner &&
                      !projects[projectId]?.myMembership?.canCreate) ||
                      projects[projectId].archived)
                  )
                }
              >
                <Icon type="plus" />
              </Button>
            )}
            <div style={{ flexGrow: 1 }} />
            {!showReport && (
              <>
                <ButtonGroup
                  style={{ float: 'right', margin: '3px' }}
                  width="undefined"
                  onSelect={this.handleChangeWhichTask}
                  selected={whichTasks as string}
                  items={{
                    all: i18n.t('all', { ns: 'taskManager' }) as string,
                    assignedToMe: i18n.t('assignedToMe', {
                      ns: 'taskManager',
                    }) as string,
                    createdByMe: i18n.t('createdByMe', {
                      ns: 'taskManager',
                    }) as string,
                    mentions: (
                      <span>
                        <TaskOptionsIcon
                          type={directMentions > 0 ? 'at-sign' : 'info'}
                          color={
                            directMentions > 0
                              ? 'red'
                              : totalMentions
                                ? 'blue'
                                : 'black'
                          }
                        />
                        <span
                          style={{
                            color:
                              directMentions > 0
                                ? 'red'
                                : totalMentions
                                  ? 'blue'
                                  : undefined,
                          }}
                        >
                          {totalMentions
                            ? convertDigitToProperFormat(totalMentions)
                            : ''}
                        </span>
                      </span>
                    ),
                  }}
                />
                {members &&
                  withTitle(
                    <APopover
                      arrow
                      curveBorder
                      placement="bottom-start"
                      trigger="click"
                      show={assigneeSelectOpen}
                      onShowChange={(e) => {
                        this.setState({ assigneeSelectOpen: e });
                      }}
                      disabled={whichTasks === 'assignedToMe'}
                      target={
                        <Button
                          color={finalAssigneeId ? 'blue' : 'blue-outline'}
                          style={{
                            margin: '4px',
                            boxShadow: 'none',
                            height: '2.2rem',
                          }}
                          disabled={whichTasks === 'assignedToMe'}
                        >
                          <Icon type="user-check" />
                          <span>
                            {finalAssigneeId
                              ? members[`USER_${finalAssigneeId}`]?.name
                              : ''}
                          </span>
                        </Button>
                      }
                    >
                      <UserSelector
                        label={''}
                        active={assigneeSelectOpen}
                        onChangeValues={this.handleChangeAssignee}
                        values={
                          finalAssigneeId
                            ? [
                                {
                                  userId: finalAssigneeId,
                                  name: members[`USER_${finalAssigneeId}`].name,
                                  roles: (
                                    members[
                                      `USER_${finalAssigneeId}`
                                    ] as IUserOption
                                  ).roles,
                                  optionType: 'user',
                                },
                              ]
                            : []
                        }
                        options={Object.values(members).filter(
                          (option) => option.optionType === 'user'
                        )}
                        disable={!!assigneeId}
                        canChangeProgress={false}
                        singleChoice={true}
                        type={'noAssignment'}
                      />
                    </APopover>,
                    i18n.t('filterAssignee', { ns: 'taskManager' }),
                    'top'
                  )}
                <Input
                  id="search"
                  type="text"
                  name="search"
                  placeholder={i18n.t('findATask', { ns: 'taskManager' })}
                  onChange={this.onSearchTaskHandler}
                  icon="search"
                  wrapperClassName="mx-1"
                  style={{ height: '2.2rem' }}
                />
                <APopover
                  arrow
                  curveBorder
                  overflowVisible
                  placement="bottom-start"
                  trigger="click"
                  target={
                    <a href="#" style={{ position: 'relative' }}>
                      <TaskOptionsIcon
                        type="sliders"
                        title={i18n.t('sortIconTitle', { ns: 'taskManager' })}
                        className={iconClassName}
                      />
                      {taskStatus !== 'all' && <InformativeBadge />}
                    </a>
                  }
                >
                  <div>
                    <p>
                      <label htmlFor="order">
                        {i18n.t('order', { ns: 'taskManager' }) as string}
                      </label>
                      <ButtonGroup
                        style={{}}
                        width="undefined"
                        onSelect={this.handleChangeOrder as any}
                        selected={order as string}
                        items={{
                          creationDate: i18n.t('creationDate', {
                            ns: 'taskManager',
                          }) as string,
                          updateDate: i18n.t('updateDate', {
                            ns: 'taskManager',
                          }) as string,
                          dueDate: i18n.t('dueDate', {
                            ns: 'taskManager',
                          }) as string,
                          startDate: i18n.t('startDate', {
                            ns: 'taskManager',
                          }) as string,
                        }}
                      />
                      <label htmlFor="order">
                        {i18n.t('sortType', { ns: 'taskManager' }) as string}
                      </label>
                      <ButtonGroup
                        style={{}}
                        width="undefined"
                        onSelect={this.handleChangeSortType}
                        selected={sortType as string}
                        items={{
                          asc: i18n.t(`${[order + '_' + 'asc']}`, {
                            ns: 'taskManager',
                          }) as string,
                          desc: i18n.t(`${[order + '_' + 'desc']}`, {
                            ns: 'taskManager',
                          }) as string,
                        }}
                      />
                    </p>
                    <p>
                      <label htmlFor="taskStatus">
                        {
                          i18n.t('basedOnStatus', {
                            ns: 'taskManager',
                          }) as string
                        }
                      </label>
                      <Select
                        options={this.dropdownOptions}
                        values={this.handleSelectValue(taskStatus)}
                        onChangeValues={this.handleOnChangeSelectValue}
                        multiChoice={false}
                        allowRemove={false}
                        inPopover
                      />
                    </p>
                    <SwitchWrapper className="switch">
                      <span>
                        {i18n.t('archived', { ns: 'taskManager' }) as string}
                      </span>
                      <label>
                        <input
                          name="archived"
                          id="archived"
                          type="checkbox"
                          checked={archived}
                          onChange={this.handleChangeInput}
                        />
                        <span
                          className="lever"
                          style={{
                            marginInlineStart: '0',
                            marginInlineEnd: '10px',
                          }}
                        />
                      </label>
                    </SwitchWrapper>
                    <SwitchWrapper className="switch">
                      <span>
                        {i18n.t('showDeleted', { ns: 'taskManager' }) as string}
                      </span>
                      <label>
                        <input
                          name="showDeleted"
                          id="showDeleted"
                          type="checkbox"
                          checked={this.props.showDeleted}
                          onChange={this.handleChangeShowDeleted}
                        />
                        <span
                          className="lever"
                          style={{
                            marginInlineStart: '0',
                            marginInlineEnd: '10px',
                          }}
                        />
                      </label>
                    </SwitchWrapper>
                  </div>
                </APopover>
                {!isModal &&
                  withTitle(
                    <Select
                      onChangeValues={(selected) => {
                        this.setState(
                          {
                            selectedDateFilter: selected[0],
                          },
                          () => {
                            this.requireTasksFromScratch();
                          }
                        );
                      }}
                      placeholder={i18n.t('year', { ns: 'taskManager' })}
                      options={this.dateFilterOptions}
                      values={selectedDateFilter ? [selectedDateFilter] : []}
                      isRequired={false}
                      allowRemove={true}
                      containerStyle={{
                        width: '100px',
                        display: 'inline-block',
                        margin: '0px 4px',
                      }}
                      inputStyle={{
                        minHeight: '28px',
                        height: '28px',
                        width: '100%',
                      }}
                    />,
                    i18n.t('yearFilter', { ns: 'taskManager' }),
                    'top'
                  )}
                {!isModal &&
                  withTitle(
                    <span className="hide-on-mobile">
                      <Select
                        onChangeValues={(selected) => {
                          this.setState(
                            {
                              selectedMonthFilter:
                                selected.length > 0
                                  ? (selected[0].value as string)
                                  : '',
                            },
                            () => {
                              this.requireTasksFromScratch();
                            }
                          );
                        }}
                        placeholder={i18n.t('month', { ns: 'taskManager' })}
                        options={this.monthFilterOptions}
                        disabled={!selectedDateFilter}
                        values={
                          selectedMonthFilter
                            ? [
                                {
                                  value: selectedMonthFilter,
                                  label: i18n.t(`${selectedMonthFilter}`, {
                                    ns: 'survey',
                                  }),
                                },
                              ]
                            : []
                        }
                        isRequired={false}
                        allowRemove={true}
                        containerStyle={{
                          width: '130px',
                          display: 'inline-block',
                          margin: '0px 4px',
                        }}
                        inputStyle={{
                          minHeight: '28px',
                          height: '28px',
                          width: '100%',
                        }}
                      />
                    </span>,
                    i18n.t('monthFilter', { ns: 'taskManager' }),
                    'top'
                  )}
              </>
            )}
            <div style={{ flexGrow: 1 }} />
            <ListOptions
              style={{
                float: 'left',
                margin: '11px 2px',
                justifyContent: 'flex-end',
              }}
              className="hide-on-mobile"
            >
              {typeof basedOnTemplate === 'string' ||
              !printTypeTemplate.includes(basedOnTemplate) ? (
                <TaskOptionsIcon
                  type="printer"
                  title={i18n.t('printIconTitle', { ns: 'taskManager' })}
                  onClick={this.handlePrintTasksList}
                  className={iconClassName}
                />
              ) : (
                <APopover
                  arrow
                  curveBorder={true}
                  target={
                    <div>
                      <TaskOptionsIcon
                        type="printer"
                        title={i18n.t('printIconTitle', { ns: 'taskManager' })}
                        className={iconClassName}
                      />
                    </div>
                  }
                  placement="bottom-end"
                  trigger="click"
                >
                  <DropdownPrintIcon>
                    <Menu>
                      <MenuItem>
                        <PrintLoaderContainer>
                          <PrintLink onClick={this.handlePrintTaskWithProgress}>
                            <Icon type="file" />
                            {messages.printWholeTasks(
                              selectedTemplateName as string
                            )}
                          </PrintLink>
                          <PrintLoaderSpinner>
                            {this.state.printLoader ? (
                              <LoadingCircleIndicatorNoPad
                                style={{
                                  margin: 0,
                                  padding: 0,
                                }}
                                innerStyle={{
                                  width: '1rem',
                                  height: '1rem',
                                }}
                              />
                            ) : (
                              <span
                                style={{
                                  width: '1rem',
                                  display: 'inline-block',
                                }}
                              ></span>
                            )}
                          </PrintLoaderSpinner>
                        </PrintLoaderContainer>
                      </MenuItem>
                      <MenuItem>
                        <PrintLink onClick={this.handlePrintTasksList}>
                          <Icon type="list" />
                          {messages.printTasks(selectedTemplateName as string)}
                        </PrintLink>
                      </MenuItem>
                    </Menu>
                  </DropdownPrintIcon>
                </APopover>
              )}
              {i18n.language === 'fa' && (
                <TaskOptionsIcon
                  type="calendar"
                  title={
                    showCalendarView
                      ? i18n.t('tasksList', { ns: 'taskManager' })
                      : i18n.t('calendar', { ns: 'taskManager' })
                  }
                  color={showCalendarView ? green : black}
                  onClick={this.handleShowCalendar}
                  className={iconClassName}
                />
              )}
              <APopover
                arrow
                curveBorder
                placement="bottom-start"
                trigger="click"
                target={
                  <a href="#">
                    <TaskOptionsIcon
                      type="copy"
                      title={i18n.t('copyTaskList', { ns: 'taskManager' })}
                      className={iconClassName}
                    />
                  </a>
                }
              >
                <div>
                  <label>
                    {i18n.t('copyTaskList', { ns: 'taskManager' }) as string}
                  </label>
                  <p className="switch my-3">
                    <>
                      <label>
                        <input
                          name="includeCompleted"
                          id="includeCompleted"
                          type="checkbox"
                          defaultChecked={true}
                          ref={this.includeCompleted}
                        />
                        <span
                          className="lever"
                          style={{
                            marginInlineStart: '0',
                            marginInlineEnd: '10px',
                          }}
                        />
                      </label>
                      {i18n.t('complete', { ns: 'taskManager' })}
                    </>
                  </p>
                  <p className="switch my-3">
                    <>
                      <label>
                        <input
                          name="includeStarted"
                          id="includeStarted"
                          type="checkbox"
                          defaultChecked={true}
                          ref={this.includeStarted}
                        />
                        <span
                          className="lever"
                          style={{
                            marginInlineStart: '0',
                            marginInlineEnd: '10px',
                          }}
                        />
                      </label>
                      {i18n.t('started', { ns: 'taskManager' })}
                    </>
                  </p>
                  <p className="switch my-3">
                    <>
                      <label>
                        <input
                          name="includeNotStarted"
                          id="includeNotStarted"
                          type="checkbox"
                          defaultChecked={true}
                          ref={this.includeNotStarted}
                        />
                        <span
                          className="lever"
                          style={{
                            marginInlineStart: '0',
                            marginInlineEnd: '10px',
                          }}
                        />
                      </label>
                      {i18n.t('notStarted', { ns: 'taskManager' })}
                    </>
                  </p>
                  <Button
                    onClick={(e) => this.handleCopyTaskList(e)}
                    color="black-outline"
                    loading={this.state.loadingCopyTask}
                  >
                    {i18n.t('copyTaskList', { ns: 'taskManager' }) as string}
                  </Button>
                </div>
              </APopover>

              {!withFewerOptions && (
                <TaskOptionsIcon
                  type={showReport ? 'list' : 'pie-chart'}
                  title={
                    showReport
                      ? (i18n.t('tasksListIconTitle', {
                          ns: 'taskManager',
                        }) as string)
                      : (i18n.t('reportIconTitle', {
                          ns: 'taskManager',
                        }) as string)
                  }
                  onClick={this.handleChangeView}
                />
              )}
            </ListOptions>
          </Header>
        </div>
        {!withFewerOptions && (
          <TemplateSelectorList
            projects={projects}
            projectId={projectId}
            loading={loading}
            onSelect={this.handleChangeTemplate}
            selectedTemplate={basedOnTemplate}
            fieldValues={fieldValues}
            boardField={boardField}
            tasks={tasks}
            company={company}
            taskCounts={taskCounts}
            showBoard={
              !showCalendarView && !showReport ? this.showBoard : undefined
            }
          />
        )}
        {showReport ? (
          <TaskReport
            compactView={!!taskSelected}
            companyId={companyId}
            activeProjectId={this.state.activeProjectId}
            handleChangeProjectId={this.handleChangeActiveProjectId}
            templateId={
              basedOnTemplate === 'all'
                ? undefined
                : (basedOnTemplate as number)
            }
            fieldValues={fieldValues}
            parentId={parentId}
          />
        ) : showCalendarView ? (
          <CalendarView
            tasks={filteredTasks}
            loading={loadingExtraTasks}
            thisUser={thisUser}
            companyId={companyId}
            projectId={projectId}
            onUpdateTask={this.handleUpdateTask}
            requireTasks={this.loadTasks}
            showProgress={showProgress}
            showWeight={showProgress}
            taskSelected={taskSelected}
            companyOwnerId={companyOwnerId}
            unfilteredTasks={tasks}
            allTasksFetched={allTasksFetched}
            handleClickOnTask={onOpenTaskEditor}
            onUpdateAssignment={this.handleUpdateAssignmentTask}
            setDateRange={this.handleSetCalendarDateRange}
            templateSelected={basedOnTemplate !== 'all'}
          />
        ) : boardField ? (
          (() => {
            const { options: optionsList, hidden } = getFieldOptions(
              boardField,
              projects,
              projectId,
              basedOnTemplate
            );
            if (hidden) return <></>;
            const options = optionsList.map((it) => it.label);
            const lanes = filteredTasks.reduce(
              (current: any[] | undefined, task: ITask) => {
                const field = task.fields.find(
                  (f: any) => f.name === boardField.name
                );
                const groups =
                  field?.type === 'multiSelect'
                    ? field?.value?.split(',') || []
                    : [field?.value];
                groups.forEach((g) => {
                  if (!options.find((a) => a === g)) g = '';
                  let lane = current!.find((it) => it.id === g);
                  if (!lane) {
                    lane = {
                      id: g,
                      title: g || i18n.t('other', { ns: 'taskManager' }),
                      cards: [],
                      allCards: [],
                    };
                    current!.push(lane);
                  }
                  lane.allCards.push(
                    this.getCardInfo(
                      task,
                      boardField.name,
                      g!,
                      isANumber(taskSelected) ? Number(taskSelected) : undefined
                    )
                  );
                });
                return current! as any[];
              },
              []
            )!;
            options.forEach((opt) => {
              if (!lanes.find((it) => it.id === opt)) {
                lanes.push({ id: opt, title: opt, cards: [] });
              }
            });
            lanes.forEach((lane) => {
              const limit = this.getLaneLimit(lane.id);
              lane.cards = lane?.allCards
                ?.sort(
                  (a: any, b: any) =>
                    (a.metadata.order || 0) - (b.metadata.order || 0)
                )
                ?.slice(0, limit);

              if (lane.cards?.length) {
                lane.cards[lane.cards?.length - 1] = {
                  ...lane.cards[lane.cards?.length - 1],
                  isLastCard: true,
                };
              }
            });
            const data = {
              lanes: lanes.sort(
                (a, b) =>
                  (a.id === '' ? options.length : options.indexOf(a.id)) -
                  (b.id === '' ? options.length : options.indexOf(b.id))
              ),
            };
            return (
              <div style={{ flex: '1', overflow: 'scroll' }}>
                {(loading || loadingExtraTasks) && (
                  <div className="progress">
                    <div className="indeterminate" />
                  </div>
                )}
                {
                  <Board
                    data={data}
                    cardDraggable={true}
                    editable={false}
                    laneDraggable={false}
                    hideCardDeleteIcon={true}
                    style={{ backgroundColor: exactWhite }}
                    onCardClick={(cardId: number) => {
                      onOpenTaskEditor(cardId);
                    }}
                    components={{
                      Card: (props: any) => (
                        <MyCard
                          {...props}
                          onLoadMoreTask={(fetchId: number, laneId: string) => {
                            if (!this.state.fetchIdList.includes(fetchId)) {
                              this.setState(
                                {
                                  fetchIdList: [
                                    ...this.state.fetchIdList,
                                    fetchId,
                                  ],
                                },
                                () => {
                                  this.loadMoreForLane(laneId, boardField);
                                }
                              );
                            }
                          }}
                        />
                      ),
                      LaneHeader: ({ title, id }: any) =>
                        this.CustomLaneHeader(
                          { title, id },
                          basedOnTemplate,
                          boardField
                        ),
                    }}
                    handleDragEnd={this.handleDragEnd(data)}
                  />
                }
              </div>
            );
          })()
        ) : (
          <>
            {loading && !loadingExtraTasks ? (
              <div className="progress">
                <div className="indeterminate" />
              </div>
            ) : (
              <div style={{ height: '4px' }} />
            )}
            <TasksList
              tasks={filteredTasks}
              loading={loading}
              thisUser={thisUser}
              companyId={companyId}
              projectId={projectId}
              onUpdateTask={this.handleUpdateTask}
              requireTasks={this.loadExtraTasks}
              showProgress={showProgress}
              showWeight={showProgress}
              taskSelected={taskSelected as any}
              companyOwnerId={companyOwnerId}
              unfilteredTasks={tasks as any}
              allTasksFetched={allTasksFetched}
              handleClickOnTask={onOpenTaskEditor}
              onUpdateAssignment={this.handleUpdateAssignmentTask}
              templateSelected={basedOnTemplate !== 'all'}
            />
          </>
        )}
      </Container>
    );
  }

  handleDragEnd =
    (data: any) =>
    (
      cardId: number,
      sourceLaneId: string,
      targetLaneId: string,
      position: number
    ) => {
      const { thisUser } = this.props;
      const userId = Number(thisUser.userId);
      const task = this.props.tasks[cardId];
      const project = this.props.projects[task.projectId];
      const projectMember: IProjectMemberView | undefined =
        project.myMembership;
      const permission = generateUserPermission(
        userId,
        task,
        projectMember?.canCreate || false,
        isCompanyOwnerOrSupport(thisUser, this.props.company),
        projectMember ? projectMember.admin : false,
        isCompanyAdmin(thisUser, this.props.company)
      );
      if (!permission.edit) {
        notify(i18n.t('editNotAllowed', { ns: 'taskManager' }), 'warning');
        return false;
      }
      const newTask: ITask = taskManipulate.storeToServer(task);
      const lane = data.lanes.find((it: any) => it.id === targetLaneId);
      const newOrder =
        position === 0
          ? lane.cards.length > 0
            ? lane.cards[position].metadata.order - 1
            : newTask.order
          : position === lane.cards.length
            ? lane.cards[position - 1].metadata.order + 1
            : (lane.cards[position - 1].metadata.order +
                lane.cards[position].metadata.order) /
              2;

      const field = newTask.fields.find(
        (it) => it.name === this.state.boardField!.name
      );
      if (field) {
        if (sourceLaneId)
          field.value = field.value?.replace(sourceLaneId, targetLaneId);
        else if (!field.value) {
          field.value = targetLaneId;
        } else field.value = `${field.value},${targetLaneId}`;
      }
      newTask.order = newOrder;
      this.props.updateTask(newTask);
      return true;
    };

  getCardInfo = (
    task: ITask,
    boardField: string,
    g: string,
    taskSelected: number | undefined
  ) => {
    const { thisUser, projects } = this.props;
    const tags = task.fields
      .map((field: IField) => {
        if (
          field.options &&
          field.value &&
          (field.name !== boardField || field.value !== g) &&
          field.value !== HiddenFieldValue
        )
          return {
            bgcolor: stringToColour(field.title!, 1, null, field.value),
            color: 'white',
            title: field.value,
            label: field.title,
          };
        else return null;
      })
      .filter((it: any) => !!it);
    const assignee = task.assignee;
    const assigneeStr = Object.values(assignee).reduce(
      (current: string, item: IAssignmentView, index: number) => {
        if (index < 3)
          return (
            current +
            (current.length ? '، ' : '') +
            this.props.users[item.userId]?.name
          );
        else if (index === 3) {
          return current + messages.otherPersons(Object.keys(item).length - 3);
        } else return current;
      },
      ''
    );
    return {
      // recently added
      task,
      project: projects[task.projectId],
      thisUser: thisUser,
      active: taskSelected === task.id,
      style: {
        backgroundColor: taskSelected === task.id ? lightGreen : undefined,
      },
      title: assigneeStr,
      description: task.title,
      id: task.id,
      laneId: g,
      label: smartDateDuration(task.startDate, task.dueDate),
      metadata: {
        order: task.order,
      },
      totalProgress: task.mainProgress,
      draft: task.draft,
      mentions: task.mentions,
      companyId: this.props.companyId,
      projectId: task.projectId,
      tags: tags,
    };
  };
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state: RootState,
  props
) => {
  const thisUser = state.thisUser;
  const tasks = state.tasks.data;
  const showDeleted = state.tasks.showDeleted;
  const users = state.users.map;
  const company = state.companies.data[props.companyId];
  const projects = company?.projects || [];
  const companyOwnerId = company?.owner?.userId;
  const dirtySet = state.tasks.dirty || [];

  const tourFlags = state.thisUser.userFlags['tour'];

  return {
    dirtySet,
    thisUser,
    tasks,
    company,
    projects,
    companyOwnerId,
    users,
    showDeleted,
    tourFlags,
  };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => ({
  updateTask: (task: ITask) => appDispatch(updateTask({ task })),
});

const ConnectedTasksListContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(TasksListContainer);

export default ConnectedTasksListContainer;
